import React from 'react';
import { createSlug } from '../../util/urlHelpers';
import { listingCardDisplayName } from '../../util/listingCardCopy';
import { listingCardPhotoUrl } from '../../util/listingCardPhoto';
import { LISTENER_STATUS, resolveListenerAvailability } from '../../util/listenerAvailability';
import { pathsFromListing, isFeaturedPath, pathLabels } from '../../util/topicPaths';
import ThumbCtas from '../ThumbCtas/ThumbCtas';
import FavoriteButton from '../FavoriteButton/FavoriteButton';
import css from './CompactListenerCard.module.css';

export const priceLine = listing => {
  const amt = listing?.attributes?.price?.amount;
  const n = typeof amt === 'number' && amt > 0 ? Math.round(amt / 100) : 10;
  return `From $${n}`;
};

const DATING_RE = /dating/i;
// Sam Oct 29 (bounce fix): never lead with Dating. On theme landings (prefer given)
// Dating is hidden and theme-relevant labels go first.
export const topicChips = (listing, max = 2, prefer = null) => {
  const paths = pathsFromListing(listing).filter(isFeaturedPath);
  const deep = [...paths].sort((a, b) => b.split('/').length - a.split('/').length);
  const all = [];
  deep.forEach(p => {
    const l = pathLabels(p).slice(-1)[0];
    if (l && !all.includes(l)) all.push(l);
  });
  const pref = Array.isArray(prefer) && prefer.length ? prefer : null;
  const rank = l => {
    if (pref) { const i = pref.findIndex(k => l.toLowerCase().includes(String(k).toLowerCase())); if (i >= 0) return i; }
    return DATING_RE.test(l) ? 1000 : 100;
  };
  const sorted = all.map((l, i) => [l, i]).sort((a, b) => rank(a[0]) - rank(b[0]) || a[1] - b[1]).map(x => x[0]);
  // FB bounce fix (Oct 30): on theme landings show only on-theme chips; fall back to 1 neutral chip.
  const matchesPref = l => pref && pref.some(k => l.toLowerCase().includes(String(k).toLowerCase()));
  const onTheme = pref ? sorted.filter(matchesPref) : [];
  const kept = pref ? (onTheme.length ? onTheme : sorted.filter(l => !DATING_RE.test(l)).slice(0, 1)) : sorted;
  return kept.slice(0, max);
};

/**
 * Compact listener card (Sam, Oct 15): small photo, name, status dot, 1–2 topic chips, price.
 * Used on /s (all modes), the talker dashboard and the home Available Now row.
 */
const CompactListenerCard = ({ listing, online, sessionBusy = false, startNowPaused = false, nextAvailableAt = null, className, variant, preferTopics = null }) => {
  if (!listing?.id?.uuid) return null;
  const title = listing.attributes?.title || '';
  const pd0 = listing.attributes?.publicData || {};
  // Sam Oct 28: home "Meet a few" shows the person's display name, never the listing title.
  const personName = String(pd0.listeningDisplayName || listing.author?.attributes?.profile?.displayName || '').trim();
  const name = variant === 'best' ? (personName || listingCardDisplayName(listing) || title) : (listingCardDisplayName(listing) || title);
  const img = listingCardPhotoUrl(listing);
  const availability = resolveListenerAvailability({ listing, online, sessionBusy, startNowPaused, nextAvailableAt });
  const available = availability.status === LISTENER_STATUS.AVAILABLE;
  const busy = availability.status === LISTENER_STATUS.IN_CONVERSATION;
  const href = `/l/${createSlug(title) || 'listener'}/${listing.id.uuid}`;
  const best = variant === 'best';
  const chips = topicChips(listing, best ? 5 : 2, preferTopics);
  const pd = listing.attributes?.publicData || {};
  const rawAbout = String((best ? listing.attributes?.description : '') || pd.profileQuote || pd.tagline || listing.attributes?.description || chips.join(' · ') || '').replace(/\s+/g, ' ').trim();
  const about = rawAbout.length > 64 ? rawAbout.slice(0, 64).replace(/\s+\S*$/, '').replace(/[\s,;:·-]+$/, '') : rawAbout;
  void busy;
  // Sam Oct 27 2026: clean thumb everywhere — photo, green dot only when available, name, one about line, ThumbCtas.
  return (
    <article className={[css.card, best ? css.best : '', css.clean, className].filter(Boolean).join(' ')} data-compact-card="true" data-clean-thumb="true" data-availability={availability.status}>
      <a href={href} className={css.link}>
        <span className={css.photoWrap}>
          {img ? <img src={img} alt="" className={css.photo} loading="lazy" /> : <span className={css.fallback} aria-hidden="true">{(name || '?').slice(0, 1)}</span>}
          {available ? <span className={css.photoDot} aria-label="Online" data-photo-dot="online" /> : null}
        </span>
        <span className={css.nameRow}>
          {available ? <span className={[css.dot, css.dotOn].join(' ')} aria-label="Available" data-card-dot="online" /> : null}
          <strong className={css.name}>{name}</strong>
        </span>
        {best ? (chips.length ? <span className={css.topics} data-card-topics="true">{chips.map(c => <span key={c} className={css.topic}>{c}</span>)}</span> : null) : (about ? <span className={css.about} data-card-about="true">{about}</span> : null)}
      </a>
      <div className={css.ctas}>
        <ThumbCtas listing={listing} availability={availability} showTopics={!best} />
      </div>
      {/* Sam Oct 28: bookmark restored on every thumb (incl. home Best strip), photo top-right corner. */}
      <span className={best ? css.saveBestCorner : css.save} data-thumb-bookmark="true">
        <FavoriteButton listing={listing} />
      </span>
    </article>
  );
};

export default CompactListenerCard;
